import React, { Children, cloneElement, useState, forwardRef } from "react";
import { createPortal } from "react-dom";
import { setRef, useEnhancedEffect, useForkRef } from "../utils";

export interface IPortalProps {
  /**
   * The children to render into the `container`.
   */
  children: React.ReactNode;
  /**
   * A node, component instance, or function that returns either.
   * The `container` will have the portal children appended to it.
   * By default, it uses the body of the top-level document object,
   * so it's simply `document.body` most of the time.
   */
  container?: React.ReactInstance | (() => React.ReactInstance | null) | null;
  /**
   * If `true`, the children stay within it's parent DOM hierarchy.
   */
  isDisabled?: boolean;
  /**
   * Callback fired once the children has been mounted into the `container`.
   */
  onRendered?: () => void;
}

const Portal: React.FC<IPortalProps> = forwardRef<any, IPortalProps>(
  ({ children, isDisabled = false, onRendered }, ref) => {
    const [mountNode, setMountNode] = useState<HTMLElement | null>(null);
    // @ts-ignore
    const handleRef = useForkRef(children ? children.ref : null, ref);

    useEnhancedEffect(() => {
      if (!isDisabled) {
        setMountNode(document.body);
      }
    }, [isDisabled]);

    useEnhancedEffect(() => {
      if (mountNode && !isDisabled) {
        setRef(ref, mountNode);
        return () => {
          setRef(ref, null);
        };
      }

      return undefined;
    }, [ref, mountNode, isDisabled]);

    useEnhancedEffect(() => {
      if (onRendered && (mountNode || isDisabled)) {
        onRendered();
      }
    }, [onRendered, mountNode, isDisabled]);

    if (isDisabled) {
      Children.only(children);
      return cloneElement(children as any, {
        ref: handleRef,
      });
    }

    return mountNode ? createPortal(children, mountNode) : mountNode;
  },
);

Portal.displayName = "Portal";

export default Portal;
